import { ColumnType, Divider, TagStatus } from '@seiue/ui'
import { Col, Row } from 'antd'
import { useState } from 'react'
import styled, { css } from 'styled-components'

import { ISearchFormItemType, TableQuery } from 'packages/components/DataTable'
import { $t } from 'packages/locale'
import {
  FieldDomainEnum,
  ReflectionRoleEnum,
  reflectionApi,
  Notification,
  InformalReflection,
} from 'packages/sdks-next/growp'

import { List } from '@/uikit/UserManagement'
import { omit } from '@seiue/util'

/**
 * 应用编辑 - 选人弹窗-弹窗内容
 *
 * @param param - 参数
 * @param param.notification - 资讯数据
 * @returns 组件
 */
export const RangeTable: React.FC<{
  notification: Notification
}> = ({ notification }) => {
  const scopes = {
    studentIds:
      notification.receverRoles?.find(
        (item) => item.role === ReflectionRoleEnum.Student
      )?.rids || [],
    teacherIds:
      notification.receverRoles?.find(
        (item) => item.role === ReflectionRoleEnum.Teacher
      )?.rids || [],
    ids: notification.rids || [],
  }

  const [role, setRole] = useState<
    ReflectionRoleEnum.Teacher | ReflectionRoleEnum.Student
  >(ReflectionRoleEnum.Student)

  const isTeacher = role === ReflectionRoleEnum.Teacher

  const teacherReceiver = notification.receverRoles?.find(
    (item) => item.role === ReflectionRoleEnum.Teacher
  )

  const studentReceiver = notification.receverRoles?.find(
    (item) => item.role === ReflectionRoleEnum.Student
  )

  return (
    <Box>
      <Row style={{ height: 'calc(100vh - 300px)' }}>
        <CheckCol span={8}>
          <Item>{$t(`共 ${scopes.ids.length} 人`)}</Item>
          <Divider />
          <SelectableItem
            $selected={!isTeacher}
            onClick={() => {
              setRole(ReflectionRoleEnum.Student)
            }}
          >
            <Label $selected={!isTeacher}>{$t('在校学生')}</Label>
            <SubLabel>
              {`已读：${studentReceiver?.readedRids?.length ?? 0} /
              ${scopes.studentIds.length ?? 0}`}
            </SubLabel>
          </SelectableItem>
          <Divider />
          <SelectableItem
            $selected={isTeacher}
            onClick={() => {
              setRole(ReflectionRoleEnum.Teacher)
            }}
          >
            <Label $selected={isTeacher}>{$t('在校教师')}</Label>
            <SubLabel>
              {`已读：${teacherReceiver?.readedRids?.length ?? 0} /
              ${scopes.teacherIds.length ?? 0}`}
            </SubLabel>
          </SelectableItem>
          <Divider />
        </CheckCol>
        <TableCol span={16}>
          <List
            key={'t'}
            domain={
              isTeacher
                ? FieldDomainEnum.FormalTeacher
                : FieldDomainEnum.FormalStudent
            }
            api={(q: TableQuery) => {
              if (isTeacher) {
                return reflectionApi.queryTeachers({
                  ...q,
                  notificationId: notification.id,
                  archivedAtNotEmpty: false,
                  expand: ['fields', 'isReadNotification'],
                  idIn: scopes.teacherIds.join(','),
                })
              }
              return reflectionApi.queryStudents({
                ...q,
                archivedAtNotEmpty: false,
                notificationId: notification.id,
                expand: ['fields', 'guardians', 'isReadNotification'],
                idIn: scopes.studentIds.join(','),
              })
            }}
            apiName={
              isTeacher
                ? 'Reflection.queryTeachers'
                : 'Reflection.queryStudents'
            }
            rowAction={[]}
            fieldSettingLocation={
              isTeacher
                ? 'InternalTeachersFieldSetting'
                : 'InternalStudentsFieldSetting'
            }
            hiddenFieldSettings={true}
            showSelectedAll={false}
            extraApiDependence={[isTeacher]}
            headerLeft={
              <Count>
                {$t(
                  `共 ${
                    isTeacher
                      ? scopes.teacherIds.length
                      : scopes.studentIds.length
                  } 条数据`
                )}
              </Count>
            }
            allowSelect={false}
            formatColumns={(columns) => {
              const newColumns: ColumnType<InformalReflection>[] = [...columns]
              // 给该列表添加「查看状态」列，初始位置在第三列，在第三列插入查看状态列
              newColumns.splice(2, 0, {
                title: $t('查看状态'),
                dataIndex: 'isReadNotification',
                key: 'isReadNotification',
                render: (isReadNotification: boolean) => {
                  return (
                    <TagStatus
                      text={isReadNotification ? $t('已读') : $t('未读')}
                      status={isReadNotification ? 'success' : 'invalid'}
                    />
                  )
                },
                width: 150,
              })

              return newColumns
            }}
            formatSearchFormConfig={(config) => {
              if (config?.['固定信息']) {
                return {
                  ...config,
                  固定信息: [
                    ...config?.['固定信息'],
                    {
                      type: ISearchFormItemType.Select,
                      label: $t('查看状态'),
                      name: 'isReadNotification',
                      otherConfig: {
                        options: [
                          {
                            label: $t('已读'),
                            value: 'true',
                          },
                          {
                            label: $t('未读'),
                            value: 'false',
                          },
                        ],
                      },
                    },
                  ],
                }
              }

              return config
            }}
            formatSearchParams={(params) => {
              const isReadNotification =
                params?.['field.isReadNotificationLike']
              return {
                ...omit(params, 'field.isReadNotificationLike'),
                isReadNotification: isReadNotification
                  ? isReadNotification === 'true'
                    ? true
                    : false
                  : undefined,
              }
            }}
          />
        </TableCol>
      </Row>
    </Box>
  )
}

const Box = styled.div`
  width: 960px;
  background-color: #fff;
  border-radius: 12px;
`

const CheckCol = styled(Col)`
  max-width: 264px;
  padding-right: 20px;
`

const Item = styled.div`
  padding: 16px;
  width: 100%;
  border-radius: 8px;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  text-align: right;
  color: ${({ theme }) => theme.text._2};
`

const SelectableItem = styled(Item)<{ $selected: boolean }>`
  &:hover {
    background-color: ${(props) => props.theme.brand._5};
  }
  background-color: ${(props) =>
    props.$selected ? props.theme.brand._5 : '#fff'};
  color: ${({ $selected, theme }) =>
    $selected ? theme.brand._1 : theme.text._1};
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const TableCol = styled(Col)`
  border-left: 1px solid ${(props) => props.theme.border.greyNormal};
  padding: 0 20px;
`

const Count = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: ${({ theme }) => theme.text._3};
`

const Label = styled.div<{ $selected?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: ${({ theme }) => theme.text._1};
  ${({ theme, $selected }) =>
    $selected &&
    css`
      background-color: ${theme.brand._5};
      color: ${theme.brand._1};
    `};
`

const SubLabel = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: ${({ theme }) => theme.text._3};
`
